import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { API_ENDPOINTS, getImageUrl } from '../../utils/GlobalAPI';
import { FaPlus, FaEdit, FaTrash, FaEye, FaEyeSlash, FaSave, FaTimes, FaUserFriends } from 'react-icons/fa';
import Slider from 'react-slick';
import 'slick-carousel/slick/slick.css';
import 'slick-carousel/slick/slick-theme.css';
import AdminPageHeader from './AdminPageHeader';
import Pagination from './Pagination';

const AdminPremiumInfluencer = () => {
  const [influencers, setInfluencers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingInfluencer, setEditingInfluencer] = useState(null);
  const [sliderKey, setSliderKey] = useState(0);
  
  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);

  const [formData, setFormData] = useState({
    name: '',
    bio: '',
    image: '',
    followers: '',
    facebook: '',
    twitter: '',
    engagement: '',
    avgViews: '',
    platform: '',
    clients: '',
    order: 0
  });

  useEffect(() => {
    fetchInfluencers();
  }, []);

  // Force re-render on resize to fix responsive bugs
  useEffect(() => {
    const handleResize = () => setSliderKey(prev => prev + 1);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const fetchInfluencers = async () => {
    try {
      const response = await fetch(API_ENDPOINTS.PREMIUM_INFLUENCERS_ADMIN);
      const data = await response.json();
      setInfluencers(data);
    } catch (error) {
      console.error('Error fetching influencers:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setFormData(prev => ({
        ...prev,
        image: file
      }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const formDataToSend = new FormData();
    
    Object.keys(formData).forEach(key => {
      if (key === 'image' && formData[key] instanceof File) {
        formDataToSend.append('image', formData[key]);
      } else if (key === 'clients' && typeof formData[key] === 'string') {
        formDataToSend.append('clients', formData[key]);
      } else {
        formDataToSend.append(key, formData[key]);
      }
    });

    try {
      const url = editingInfluencer 
        ? `${API_ENDPOINTS.PREMIUM_INFLUENCERS}/${editingInfluencer._id}`
        : API_ENDPOINTS.PREMIUM_INFLUENCERS;
      
      const method = editingInfluencer ? 'PUT' : 'POST';
      
      const response = await fetch(url, {
        method,
        body: formDataToSend
      });

      if (response.ok) {
        fetchInfluencers();
        resetForm();
        setShowModal(false);
      }
    } catch (error) {
      console.error('Error saving influencer:', error);
    }
  };

  const handleEdit = (influencer) => {
    setEditingInfluencer(influencer);
    setFormData({
      name: influencer.name,
      bio: influencer.bio,
      image: influencer.image,
      followers: influencer.followers,
      facebook: influencer.facebook || '',
      twitter: influencer.twitter || '',
      engagement: influencer.engagement,
      avgViews: influencer.avgViews,
      platform: influencer.platform,
      clients: influencer.clients.join(', '),
      order: influencer.order
    });
    setShowModal(true);
  };

  const handleDelete = async (id) => {
    if (window.confirm('Are you sure you want to delete this influencer?')) {
      try {
        await fetch(`${API_ENDPOINTS.PREMIUM_INFLUENCERS}/${id}`, {
          method: 'DELETE'
        });
        fetchInfluencers();
      } catch (error) {
        console.error('Error deleting influencer:', error);
      }
    }
  };

  const handleToggleStatus = async (id) => {
    try {
      await fetch(`${API_ENDPOINTS.PREMIUM_INFLUENCERS}/${id}/toggle`, {
        method: 'PATCH'
      });
      fetchInfluencers();
    } catch (error) {
      console.error('Error toggling status:', error);
    }
  };

  const resetForm = () => {
    setFormData({
      name: '',
      bio: '',
      image: '',
      followers: '',
      facebook: '',
      twitter: '',
      engagement: '',
      avgViews: '',
      platform: '',
      clients: '',
      order: 0
    });
    setEditingInfluencer(null);
  };

  const settings = {
    key: sliderKey,
    dots: true,
    infinite: true,
    speed: 600,
    autoplay: true,
    autoplaySpeed: 4000,
    arrows: false,
    slidesToShow: 1,
    slidesToScroll: 1,
    adaptiveHeight: true,
    pauseOnHover: true,
    swipe: true,
    draggable: true,
    responsive: [
      {
        breakpoint: 768,
        settings: {
          slidesToShow: 1,
        },
      },
    ],
  };

  // Reset page when influencers list size changes
  useEffect(() => {
    setCurrentPage(1);
  }, [influencers.length]);

  // Pagination logic
  const totalPages = Math.ceil(influencers.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const paginatedInfluencers = influencers.slice(startIndex, endIndex);

  const handlePageChange = (page, newItemsPerPage = itemsPerPage) => {
    setCurrentPage(page);
    if (newItemsPerPage !== itemsPerPage) {
      setItemsPerPage(newItemsPerPage);
      setCurrentPage(1);
    }
  };

  if (loading) {
    return (
      <div className="bg-slate-50/50 min-h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-orange-500 mx-auto mb-4"></div>
          <p className="text-gray-600">Loading premium roster...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-gray-50 min-h-screen">
      <AdminPageHeader
        title="Premium Influencers Management"
        subtitle="Manage and showcase premium influencers"
        icon={<FaUserFriends className="w-6 h-6" />}
      >
        <button
          onClick={() => {
            resetForm();
            setShowModal(true);
          }}
          className="bg-gradient-to-r from-orange-300 via-pink-300 to-purple-300 text-white px-4 py-2 rounded-lg hover:opacity-90 flex items-center gap-2 shadow-md"
        >
          <FaPlus /> Add New Influencer
        </button>
      </AdminPageHeader>
      <div className="pt-32 p-6 max-w-7xl mx-auto">

        {/* Frontend Preview Section */}
        <div className="mb-8">
          <h2 className="text-2xl font-bold text-gray-800 mb-4">Frontend Preview</h2>
          <div className="bg-white rounded-lg shadow-lg p-6">
            {influencers.length === 0 ? (
              <div className="text-center py-8">
                <h3 className="text-xl text-gray-600">No premium influencers available</h3>
                <p className="text-gray-500 mt-2">Add some influencers to see the preview</p>
              </div>
            ) : (
              <section className="w-full px-4 py-12 bg-gray-50">
                <div className="max-w-6xl mx-auto text-center mb-10">
                  <h2 className="text-3xl md:text-4xl font-semibold text-gray-800">Meet Our Premium Influencers</h2>
                  <p className="text-gray-500 mt-3">
                    Discover top talent with proven engagement, global reach, and industry-leading partnerships.
                  </p>
                </div>

                <div className="max-w-5xl mx-auto">
                  <Slider {...settings}>
                    {influencers.filter(inf => inf.isActive).map((inf, idx) => (
                      <div key={idx}>
                        <div className="bg-white shadow-xl rounded-2xl p-6 md:p-10 flex flex-col md:flex-row items-center gap-8 transition-all duration-300 min-h-[360px]">
                          <img
                            src={getImageUrl(inf.image)}
                            alt={inf.name}
                            className="w-40 h-40 rounded-full object-cover border-4 border-orange-100 shadow-md"
                            onError={(e) => {
                              e.target.onerror = null;
                              e.target.src = "data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 24 24' fill='%23cbd5e1'%3E%3Cpath d='M12 12c2.21 0 4-1.79 4-4s-1.79-4-4-4-4 1.79-4 4 1.79 4 4 4zm0 2c-2.67 0-8 1.34-8 4v2h16v-2c0-2.66-5.33-4-8-4z'/%3E%3C/svg%3E";
                            }}
                          />
                          <div className="text-left w-full">
                            <h3 className="text-2xl font-bold text-gray-800">{inf.name}</h3>
                            <p className="text-gray-600 mt-2">{inf.bio}</p>

                            <div className="mt-4 grid grid-cols-2 sm:grid-cols-3 gap-x-4 gap-y-2 text-sm text-gray-700">
                              <div><span className="font-semibold">{inf.followers}</span> Followers</div>
                              <div><span className="font-semibold">{inf.engagement}%</span> Engagement</div>
                              <div><span className="font-semibold">{inf.avgViews}/Views</span> Avg Views</div>
                              <div className="col-span-2 sm:col-span-1">
                                <span className="font-semibold">{inf.platform}</span>
                              </div>
                            </div>
                            
                            <div className="mt-4 flex gap-3 text-gray-600">
                              {inf.facebook && (
                                <a href={inf.facebook.includes('http') ? inf.facebook : `https://${inf.facebook}`} target="_blank" rel="noopener noreferrer" className="hover:text-blue-600 transition-colors">
                                  <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24"><path d="M22 12c0-5.52-4.48-10-10-10S2 6.48 2 12c0 4.84 3.44 8.87 8 9.8V15H8v-3h2V9.5C10 7.57 11.57 6 13.5 6H16v3h-2c-.55 0-1 .45-1 1v2h3v3h-3v6.95c5.05-.5 9-4.76 9-9.95z"/></svg>
                                </a>
                              )}
                              {inf.twitter && (
                                <a href={inf.twitter.includes('http') ? inf.twitter : `https://${inf.twitter}`} target="_blank" rel="noopener noreferrer" className="hover:text-blue-400 transition-colors">
                                  <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24"><path d="M23.95 4.57c-.88.39-1.83.65-2.83.77 1.02-.61 1.8-1.58 2.17-2.73-.95.56-2.01.97-3.13 1.19-.9-.96-2.18-1.56-3.59-1.56-2.71 0-4.92 2.21-4.92 4.92 0 .39.04.77.13 1.13-4.09-.21-7.72-2.16-10.15-5.14-.42.72-.66 1.56-.66 2.47 0 1.71.87 3.22 2.19 4.1-.8-.03-1.56-.25-2.22-.61v.06c0 2.38 1.69 4.36 3.94 4.81-.41.11-.85.17-1.3.17-.32 0-.62-.03-.92-.09.63 1.95 2.44 3.37 4.59 3.41-1.68 1.32-3.8 2.11-6.11 2.11-.4 0-.79-.02-1.18-.07 2.17 1.39 4.76 2.2 7.55 2.2 9.06 0 14.01-7.51 14.01-14.01 0-.21-.01-.42-.03-.63.96-.69 1.8-1.56 2.46-2.55z"/></svg>
                                </a>
                              )}
                            </div>

                            <div className="mt-5">
                              <p className="font-semibold text-gray-800 mb-2">Worked with:</p>
                              <div className="flex flex-wrap gap-2">
                                {inf.clients.map((client) => (
                                  <span
                                    key={client}
                                    className="bg-gradient-to-r from-orange-100 to-pink-100 text-orange-700 px-3 py-1 text-xs rounded-full font-medium"
                                  >
                                    {client}
                                  </span>
                                ))}
                              </div>
                            </div>

                            <a
                              href="#"
                              className="inline-block mt-6 px-6 py-3 bg-gradient-to-r from-orange-300 via-pink-300 to-purple-300 text-white text-sm rounded-xl shadow-md hover:opacity-90 transition"
                            >
                              Hire Me
                            </a>
                          </div>
                        </div>
                      </div>
                    ))}
                  </Slider>
                </div>
              </section>
            )}
          </div>
        </div>

        {/* Admin Management Section */}
        <div className="bg-white rounded-lg shadow overflow-hidden">
          <div className="px-6 py-4 border-b border-gray-200">
            <h2 className="text-xl font-semibold text-gray-800">Manage Influencers</h2>
          </div>
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Image</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Name</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Bio</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Followers</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Platform</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Socials</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {paginatedInfluencers.map((influencer) => (
                  <tr key={influencer._id} className={!influencer.isActive ? 'opacity-50' : ''}>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <img
                        src={getImageUrl(influencer.image)}
                        alt={influencer.name}
                        className="h-12 w-12 rounded-full object-cover"
                        onError={(e) => {
                          e.target.onerror = null;
                          e.target.src = "data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 24 24' fill='%23cbd5e1'%3E%3Cpath d='M12 12c2.21 0 4-1.79 4-4s-1.79-4-4-4-4 1.79-4 4 1.79 4 4 4zm0 2c-2.67 0-8 1.34-8 4v2h16v-2c0-2.66-5.33-4-8-4z'/%3E%3C/svg%3E";
                        }}
                      />
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                      {influencer.name}
                    </td>
                    <td className="px-6 py-4 text-sm text-gray-500 max-w-xs truncate">
                      {influencer.bio}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {influencer.followers}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {influencer.platform}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      <div className="flex gap-2">
                        {influencer.facebook && <span className="text-blue-600">FB</span>}
                        {influencer.twitter && <span className="text-blue-400">X</span>}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <button
                        onClick={() => handleToggleStatus(influencer._id)}
                        className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                          influencer.isActive
                            ? 'bg-green-100 text-green-800'
                            : 'bg-red-100 text-red-800'
                        }`}
                      >
                        {influencer.isActive ? <FaEye className="mr-1" /> : <FaEyeSlash className="mr-1" />}
                        {influencer.isActive ? 'Active' : 'Inactive'}
                      </button>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                      <div className="flex space-x-2">
                        <button
                          onClick={() => handleEdit(influencer)}
                          className="text-orange-500 hover:text-orange-700"
                        >
                          <FaEdit />
                        </button>
                        <button
                          onClick={() => handleDelete(influencer._id)}
                          className="text-red-600 hover:text-red-900"
                        >
                          <FaTrash />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        {/* Pagination */}
        <div className="mt-4">
          <Pagination
            currentPage={currentPage}
            totalPages={totalPages}
            onPageChange={handlePageChange}
            itemsPerPage={itemsPerPage}
            totalItems={influencers.length}
            showItemsPerPage={true}
            itemsPerPageOptions={[5, 10, 20, 50]}
          />
        </div>

        <AnimatePresence>
          {showModal && (
            <>
              {/* Backdrop */}
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                transition={{ duration: 0.2 }}
                className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50"
                onClick={() => {
                  setShowModal(false);
                  resetForm();
                }}
              />
              
              {/* Modal */}
              <div className="fixed inset-0 z-50 flex items-center justify-center p-4 pointer-events-none">
                <motion.div
                  initial={{ opacity: 0, scale: 0.95, y: 20 }}
                  animate={{ opacity: 1, scale: 1, y: 0 }}
                  exit={{ opacity: 0, scale: 0.95, y: 20 }}
                  transition={{ 
                    type: "spring", 
                    damping: 25, 
                    stiffness: 300,
                    duration: 0.3
                  }}
                  className="relative w-full max-w-4xl bg-white rounded-2xl shadow-2xl max-h-[90vh] overflow-hidden flex flex-col pointer-events-auto"
                  onClick={(e) => e.stopPropagation()}
                >
              <div className="flex justify-between items-center p-6 border-b border-gray-200 bg-gradient-to-r from-orange-50/50 via-pink-50/50 to-purple-50/50">
                <h3 className="text-xl font-bold text-gray-800">
                  {editingInfluencer ? 'Edit Influencer' : 'Add New Influencer'}
                </h3>
                <button
                  onClick={() => {
                    setShowModal(false);
                    resetForm();
                  }}
                  className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
                >
                  <FaTimes size={20} />
                </button>
              </div>

              <div className="p-6 flex-1 overflow-y-auto">
                <div className="mb-4 p-3 bg-gradient-to-r from-orange-50 to-pink-50 border border-orange-200 rounded-md">
                  <p className="text-sm text-orange-800">
                    <strong>Note:</strong> Enter only the values (e.g., "50K", "6.1", "90K"). The suffixes (/mo, %, /Views) will be added automatically in the display.
                  </p>
                </div>
                <form id="influencer-form" onSubmit={handleSubmit} className="space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700">Name</label>
                    <input
                      type="text"
                      name="name"
                      value={formData.name}
                      onChange={handleInputChange}
                      required
                      className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm !text-gray-900 !bg-white focus:ring-orange-400 focus:border-orange-400 outline-none placeholder:text-gray-500"
                      style={{ color: '#111827', backgroundColor: '#ffffff' }}
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700">Followers</label>
                    <input
                      type="text"
                      name="followers"
                      value={formData.followers}
                      onChange={handleInputChange}
                      required
                      className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm !text-gray-900 !bg-white focus:ring-orange-400 focus:border-orange-400 outline-none placeholder:text-gray-500"
                      style={{ color: '#111827', backgroundColor: '#ffffff' }}
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700">Facebook URL</label>
                    <input
                      type="text"
                      name="facebook"
                      value={formData.facebook}
                      onChange={handleInputChange}
                      placeholder="e.g., https://facebook.com/username"
                      className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm !text-gray-900 !bg-white focus:ring-orange-400 focus:border-orange-400 outline-none placeholder:text-gray-500"
                      style={{ color: '#111827', backgroundColor: '#ffffff' }}
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700">X (Twitter) URL</label>
                    <input
                      type="text"
                      name="twitter"
                      value={formData.twitter}
                      onChange={handleInputChange}
                      placeholder="e.g., https://x.com/username"
                      className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm !text-gray-900 !bg-white focus:ring-orange-400 focus:border-orange-400 outline-none placeholder:text-gray-500"
                      style={{ color: '#111827', backgroundColor: '#ffffff' }}
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700">Engagement (will show as "value%")</label>
                    <input
                      type="text"
                      name="engagement"
                      value={formData.engagement}
                      onChange={handleInputChange}
                      placeholder="e.g., 6.1"
                      required
                      className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm !text-gray-900 !bg-white focus:ring-orange-400 focus:border-orange-400 outline-none placeholder:text-gray-500"
                      style={{ color: '#111827', backgroundColor: '#ffffff' }}
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700">Average Views (will show as "value/Views")</label>
                    <input
                      type="text"
                      name="avgViews"
                      value={formData.avgViews}
                      onChange={handleInputChange}
                      placeholder="e.g., 90K"
                      required
                      className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm !text-gray-900 !bg-white focus:ring-orange-400 focus:border-orange-400 outline-none placeholder:text-gray-500"
                      style={{ color: '#111827', backgroundColor: '#ffffff' }}
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700">Platform</label>
                    <input
                      type="text"
                      name="platform"
                      value={formData.platform}
                      onChange={handleInputChange}
                      required
                      className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm !text-gray-900 !bg-white focus:ring-orange-400 focus:border-orange-400 outline-none placeholder:text-gray-500"
                      style={{ color: '#111827', backgroundColor: '#ffffff' }}
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700">Order</label>
                    <input
                      type="number"
                      name="order"
                      value={formData.order}
                      onChange={handleInputChange}
                      className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm !text-gray-900 !bg-white focus:ring-orange-400 focus:border-orange-400 outline-none placeholder:text-gray-500"
                      style={{ color: '#111827', backgroundColor: '#ffffff' }}
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700">Bio</label>
                  <textarea
                    name="bio"
                    value={formData.bio}
                    onChange={handleInputChange}
                    required
                    rows={3}
                    className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm !text-gray-900 !bg-white focus:ring-orange-400 focus:border-orange-400 outline-none resize-none"
                    style={{ color: '#111827', backgroundColor: '#ffffff' }}
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700">Clients (comma-separated)</label>
                  <input
                    type="text"
                    name="clients"
                    value={formData.clients}
                    onChange={handleInputChange}
                    placeholder="Apple, Google, Meta"
                    className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm !text-gray-900 !bg-white focus:ring-orange-400 focus:border-orange-400 outline-none placeholder:text-gray-500"
                    style={{ color: '#111827', backgroundColor: '#ffffff' }}
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700">Image</label>
                  <input
                    type="file"
                    accept="image/*"
                    onChange={handleImageChange}
                    className="mt-1 block w-full text-sm text-gray-500 file:mr-4 file:py-2 file:px-4 file:rounded-full file:border-0 file:text-sm file:font-semibold file:bg-gradient-to-r file:from-orange-100 file:via-pink-100 file:to-purple-100 file:text-orange-600 hover:file:opacity-80"
                  />
                  {formData.image && typeof formData.image === 'string' && (
                    <img src={formData.image} alt="Current" className="mt-2 h-20 w-20 object-cover rounded" />
                  )}
                </div>

                </form>
              </div>
              <div className="flex items-center justify-end gap-3 p-6 border-t border-gray-200 bg-gray-50">
                <button
                  type="button"
                  onClick={() => {
                    setShowModal(false);
                    resetForm();
                  }}
                  className="px-5 py-2.5 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-100 transition-colors font-medium"
                >
                  Cancel
                </button>
                <button
                  type="button"
                  onClick={(e) => {
                    e.preventDefault();
                    const form = document.getElementById('influencer-form');
                    if (form) {
                      form.requestSubmit();
                    } else {
                      handleSubmit(e);
                    }
                  }}
                  className="px-5 py-2.5 bg-gradient-to-r from-orange-300 via-pink-300 to-purple-300 hover:opacity-90 text-white rounded-lg transition-all font-medium shadow-md flex items-center gap-2"
                >
                  <FaSave /> {editingInfluencer ? 'Update' : 'Create'}
                </button>
              </div>
                </motion.div>
              </div>
            </>
          )}
        </AnimatePresence>
      </div>
    </div>
  );
};

export default AdminPremiumInfluencer;
